/**
 * Map existing dashboard API payloads → rankSorenCards input.
 * Pending detail: notifications (hub_upload / sub_doc_upload) — count-only
 * GET /api/dashboard/pending-sub-uploads cannot yield per-sub cards without
 * a server shape change (forbidden for this PR).
 */
import type { FollowUpItem, LienAlert, Project } from '../../api/types'
import type { AppNotification } from '../../api/notifications'
import type {
  RankSorenInput,
  SorenCard,
  SorenFollowUp,
  SorenLien,
  SorenPendingUpload,
} from './rankSorenCards'
import { appendSorenSource } from './sorenSession'

function ownerFromProject(
  projectId: number,
  projectsById: Map<number, Project>,
  fallbackEmail?: string | null,
): { owner_key: string; owner_name: string } {
  const p = projectsById.get(projectId)
  const name = (p?.owner || '').trim() || fallbackEmail || 'Owner'
  const key = (p?.owner || fallbackEmail || `project-${projectId}`).toLowerCase()
  return { owner_key: key, owner_name: name }
}

export function mapFollowUps(
  queue: FollowUpItem[],
  projects: Project[] = [],
): SorenFollowUp[] {
  const byId = new Map(projects.map((p) => [p.id, p]))
  return queue.map((q) => {
    const { owner_key, owner_name } = ownerFromProject(
      q.project_id,
      byId,
      q.owner_email || q.follow_up_recipient_email,
    )
    return {
      pay_app_id: q.pay_app_id,
      project_id: q.project_id,
      project_name: q.project_name,
      amount_due: Number(q.amount_due) || 0,
      days_overdue: q.days_overdue,
      app_number: q.app_number ?? null,
      owner_key,
      owner_name,
    }
  })
}

export function mapLiens(alerts: LienAlert[], projects: Project[] = []): SorenLien[] {
  const byId = new Map(projects.map((p) => [p.id, p]))
  return alerts.map((a) => {
    const p = byId.get(a.project_id)
    const contract = p?.original_contract != null ? parseFloat(String(p.original_contract)) : null
    return {
      id: a.id,
      project_id: a.project_id,
      project_name: a.project_name,
      days_remaining: a.days_remaining,
      deadline_date: a.deadline_date,
      filed_at: a.filed_at,
      contract_amount: Number.isFinite(contract as number) ? (contract as number) : null,
    }
  })
}

/** Parse sub name from notification title: "{sub} uploaded {label} — {project}". */
export function parseSubUploadTitle(title: string): { subName: string; docLabel: string } | null {
  const m = title.match(/^(.+?) uploaded (.+?)(?:\s+[—\-–]\s+.+)?$/i)
  if (!m) return null
  return { subName: m[1].trim(), docLabel: m[2].trim() }
}

export function mapPendingFromNotifications(
  notifications: AppNotification[],
): SorenPendingUpload[] {
  const out: SorenPendingUpload[] = []
  for (const n of notifications) {
    if (n.read_at) continue
    if (n.entity_type !== 'hub_upload' && n.event_type !== 'sub_doc_upload') continue
    // Skip synthetic Soren lifecycle rows
    if (n.group_key?.startsWith('soren:')) continue
    const pm = n.deep_link?.match(/[?&]pm=(\d+)/)
    const projectId = pm ? parseInt(pm[1], 10) : NaN
    if (!Number.isFinite(projectId)) continue
    const parsed = parseSubUploadTitle(n.title || '')
    const subName = parsed?.subName || 'Sub'
    const projectName =
      (n.title || '').split(/[—\-–]/).pop()?.trim() ||
      n.body ||
      `Project ${projectId}`
    out.push({
      subKey: subName.toLowerCase().replace(/\s+/g, '-'),
      subName,
      projectId,
      projectName,
      uploadedAt: n.created_at,
      docCount: 1,
      docLabels: parsed?.docLabel ? [parsed.docLabel] : undefined,
      notificationIds: [n.id],
    })
  }
  return out
}

export function mapReviewNudges(
  rows: Array<{
    id?: string
    kind?: string
    project_id?: number
    what?: string
    why?: string
    do?: Array<{ label: string; href: string | null; action?: string }>
    amount?: { amount?: number } | number
    days_remaining?: number
    deadline_date?: string | null
    placement?: string
    _soren_placement?: string
    week_of?: string
  }> = [],
): SorenCard[] {
  const out: SorenCard[] = []
  for (const row of rows) {
    const allowed = [
      'cadence', 'retainage', 'cash_delta', 'friday_checkin', 'deadline',
    ]
    if (!row.kind || !allowed.includes(row.kind)) continue
    const projectId = Number(row.project_id)
    const hasProject = Number.isFinite(projectId)
    const doLinks = (row.do || [])
      .filter((d) => d.href)
      .map((d) => ({
        label: d.label,
        href: appendSorenSource(String(d.href)),
      }))
    const primary =
      doLinks[0] ||
      (hasProject
        ? { label: 'Open project', href: appendSorenSource(`/projects?pm=${projectId}`) }
        : { label: 'Open Soren', href: appendSorenSource('/dashboard?from=soren') })
    const amt = typeof row.amount === 'number'
      ? row.amount
      : row.amount?.amount
    out.push({
      id: row.id || `${row.kind}-${hasProject ? projectId : 'cash'}`,
      kind: row.kind as SorenCard['kind'],
      title: row.what || '',
      body: row.why || '',
      what: row.what,
      why: row.why,
      doActions: [
        ...doLinks,
        ...(row.do || [])
          .filter((d) => !d.href && (
            d.label === 'Remind me'
            || d.action === 'skip_friday_checkin'
            || d.label === 'Skip this week'
          ))
          .map((d) => ({ label: d.label, href: '' })),
      ],
      primary,
      secondary: doLinks[1],
      projectId: hasProject ? projectId : 0,
      notificationIds: [],
      amount: amt,
      daysRemaining: row.days_remaining,
      preferDoFirst: row.placement === 'do_first' || row._soren_placement === 'do_first',
      weekOf: row.week_of,
    })
  }
  return out
}

export function buildRankInput(opts: {
  queue?: FollowUpItem[]
  liens?: LienAlert[]
  projects?: Project[]
  notifications?: AppNotification[]
  unreadCount?: number
  userName?: string | null
  now?: Date
  reviewNudges?: SorenCard[]
}): RankSorenInput {
  const projects = opts.projects || []
  return {
    followUps: mapFollowUps(opts.queue || [], projects),
    liens: mapLiens(opts.liens || [], projects),
    pendingUploads: mapPendingFromNotifications(opts.notifications || []),
    reviewNudges: opts.reviewNudges || [],
    unreadNotificationCount: opts.unreadCount || 0,
    userName: opts.userName,
    now: opts.now,
  }
}
